import React, { useState, useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { Share2, Download, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

// Only use same-origin WebP animations in exported frames (cross-origin GIFs
// would taint the canvas and block the PNG export).
const safeMedia = (pr) => {
  const w = pr.animation_webp;
  if (w && w.startsWith('/')) return `${BACKEND_URL}/api${w}`;
  return null;
};

const CAT = {
  chest: { es: 'Pecho', c: '#dc2626' }, back: { es: 'Espalda', c: '#2563eb' },
  legs: { es: 'Pierna', c: '#059669' }, shoulders: { es: 'Hombro', c: '#ea580c' },
  arms: { es: 'Brazo', c: '#7c3aed' }, core: { es: 'Core', c: '#ca8a04' },
  'full-body': { es: 'Cuerpo completo', c: '#0d9488' }, other: { es: 'Otros', c: '#71717a' },
};

const FRAME_W = 1080;
const FRAME_H = 1920;

// ── individual 9:16 frames (rendered at true 1080×1920) ─────────
const FrameShell = ({ children, kicker }) => (
  <div
    style={{ width: FRAME_W, height: FRAME_H }}
    className="relative bg-[#050505] text-white flex flex-col overflow-hidden font-manrope"
  >
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.10),transparent_55%)]" />
    <div className="relative flex-1 flex flex-col px-20 py-24">{children}</div>
    <div className="relative px-20 pb-16 flex items-center justify-between">
      <span className="font-oswald uppercase tracking-[0.4em] text-[#D4AF37] text-2xl">End of an Era</span>
      <span className="font-manrope text-[#52525B] text-xl">{kicker}</span>
    </div>
  </div>
);

const CoverFrame = ({ s, quote }) => (
  <FrameShell kicker={s.center}>
    <div className="flex-1 flex flex-col justify-center">
      <p className="font-oswald uppercase tracking-[0.5em] text-[#D4AF37] text-3xl mb-10">Una etapa que dejó huella</p>
      <h1 className="font-anton uppercase text-white leading-[0.92]" style={{ fontSize: 130 }}>{s.client.name}</h1>
      <p className="font-manrope text-[#A1A1AA] text-4xl mt-12">{fmtDate(s.period.start)} — {fmtDate(s.period.end)}</p>
      <p className="font-manrope text-[#A1A1AA] text-3xl mt-3">{s.period.time_together} de viaje</p>
      {quote && <p className="font-cormorant italic text-white/80 mt-16 leading-snug" style={{ fontSize: 52 }}>“{quote}”</p>}
    </div>
  </FrameShell>
);

const StatsFrame = ({ s }) => {
  const j = s.journey;
  const rows = [
    [j.total_sessions, 'Sesiones completadas'],
    [j.active_weeks, 'Semanas activas'],
    [`${j.hours_trained}h`, 'Horas entrenadas'],
    [`${j.adherence_percent}%`, 'Adherencia'],
    [j.cycles_completed, 'Ciclos completados'],
    [s.achievements.pr_count, 'Récords personales'],
  ];
  return (
    <FrameShell kicker="El viaje en números">
      <h2 className="font-oswald uppercase tracking-tight text-[#D4AF37] mb-16" style={{ fontSize: 64 }}>El viaje</h2>
      <div className="flex-1 flex flex-col justify-center gap-10">
        {rows.map(([v, l]) => (
          <div key={l} className="flex items-baseline justify-between border-b border-white/10 pb-6">
            <span className="font-manrope text-[#A1A1AA] text-4xl">{l}</span>
            <span className="font-anton text-white" style={{ fontSize: 96 }}>{v}</span>
          </div>
        ))}
      </div>
    </FrameShell>
  );
};

const CategoryPRFrame = ({ catKey, cards }) => {
  const meta = CAT[catKey] || CAT.other;
  const list = cards.slice(0, 10);
  return (
    <FrameShell kicker="Salón de la fama">
      <div className="flex items-center gap-6 mb-12 pb-5 border-b border-[#D4AF37]/15">
        <div style={{ width: 16, height: 64, borderRadius: 8, background: meta.c, boxShadow: `0 0 22px ${meta.c}aa` }} />
        <h2 className="font-oswald uppercase tracking-tight text-white" style={{ fontSize: 80 }}>{meta.es}</h2>
        <span className="font-manrope text-[#D4AF37]/60 ml-auto uppercase tracking-wide" style={{ fontSize: 30 }}>
          {cards.length} {cards.length === 1 ? 'récord' : 'récords'}
        </span>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-6 content-start">
        {list.map((pr) => {
          const media = safeMedia(pr);
          return (
            <div key={pr.exercise_id} className="rounded-2xl bg-[#0c0c0c] overflow-hidden" style={{ borderTop: `6px solid ${meta.c}` }}>
              <div className="relative bg-white/5 flex items-center justify-center" style={{ height: 170 }}>
                {media
                  ? <img src={media} alt="" crossOrigin="anonymous" className="w-full h-full object-contain p-2" />
                  : <span className="text-[#D4AF37]/40" style={{ fontSize: 70 }}>🏆</span>}
                <span className="absolute top-3 right-4 font-anton text-white" style={{ fontSize: 48 }}>
                  {pr.weight}<span className="text-[#D4AF37]" style={{ fontSize: 24, verticalAlign: 'top' }}>kg</span>
                </span>
              </div>
              <div className="px-5 py-4">
                <p className="font-oswald uppercase text-white leading-tight" style={{ fontSize: 24, minHeight: 56 }}>{pr.exercise_name}</p>
                <p className="font-manrope text-[#A1A1AA] mt-1" style={{ fontSize: 20 }}>
                  {pr.reps} reps{pr.improvement_pct ? ` · +${pr.improvement_pct}%` : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </FrameShell>
  );
};

const MilestonesFrame = ({ s }) => (
  <FrameShell kicker="Logros desbloqueados">
    <h2 className="font-oswald uppercase tracking-tight text-[#D4AF37] mb-14" style={{ fontSize: 64 }}>Logros</h2>
    <div className="flex-1 grid grid-cols-2 gap-8 content-center">
      {(s.milestones || []).map((m) => (
        <div
          key={m.key}
          className="flex items-center gap-6 rounded-2xl px-8 py-7"
          style={{
            background: m.unlocked ? 'linear-gradient(135deg,rgba(212,175,55,0.20),transparent)' : 'rgba(255,255,255,0.04)',
            border: m.unlocked ? '2px solid rgba(212,175,55,0.5)' : '2px solid rgba(255,255,255,0.08)',
            opacity: m.unlocked ? 1 : 0.4,
          }}
        >
          <span style={{ fontSize: 64 }}>{m.icon}</span>
          <span className="font-oswald uppercase text-white leading-tight" style={{ fontSize: 30 }}>{m.label}</span>
        </div>
      ))}
    </div>
  </FrameShell>
);

const ClosingFrame = ({ s }) => (
  <FrameShell kicker={s.center}>
    <div className="flex-1 flex flex-col justify-center text-center">
      <h2 className="font-oswald uppercase tracking-tight text-white mb-12" style={{ fontSize: 72 }}>¿Qué viene ahora?</h2>
      <div className="font-cormorant text-white/85 leading-relaxed space-y-5" style={{ fontSize: 46 }}>
        <p>No sabemos qué caminos tomará la vida a partir de aquí.</p>
        <p>Quizá vuelvas a entrenar con nosotros.</p>
        <p className="text-[#D4AF37]">Pero todo lo que construiste aquí ya forma parte de ti.</p>
      </div>
      <div className="mt-20 space-y-4 text-2xl">
        <p className="text-[#A1A1AA]">Cliente <span className="text-white ml-4">{s.client.name}</span></p>
        <p className="text-[#A1A1AA]">Entrenador <span className="text-white ml-4">{s.trainer.name}</span></p>
      </div>
    </div>
  </FrameShell>
);

// ── share button + modal studio ─────────────────────────────────
const StoryShareButton = ({ report }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const refs = useRef([]);

  const s = report?.snapshot;

  // Group PRs by category (preserving the category-ordered backend list)
  const prGroups = [];
  if (s) {
    const idx = {};
    (s.hall_of_fame || []).forEach((pr) => {
      const c = pr.category || 'other';
      if (!(c in idx)) { idx[c] = prGroups.length; prGroups.push([c, []]); }
      prGroups[idx[c]][1].push(pr);
    });
  }

  const slug = (str) => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');

  const frames = s ? [
    { name: 'portada', label: 'Portada', node: <CoverFrame s={s} quote={report.optional_quote} /> },
    { name: 'resumen', label: 'Resumen', node: <StatsFrame s={s} /> },
    ...prGroups.map(([key, cards]) => {
      const meta = CAT[key] || CAT.other;
      return { name: `marcas-${slug(meta.es)}`, label: meta.es, node: <CategoryPRFrame catKey={key} cards={cards} /> };
    }),
    { name: 'logros', label: 'Logros', node: <MilestonesFrame s={s} /> },
    { name: 'cierre', label: 'Cierre', node: <ClosingFrame s={s} /> },
  ] : [];

  const exportOne = useCallback(async (node, name) => {
    const dataUrl = await toPng(node, {
      width: FRAME_W, height: FRAME_H, pixelRatio: 1, backgroundColor: '#050505',
    });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `eoae-${name}.png`;
    a.click();
  }, []);

  const downloadAll = useCallback(async () => {
    setBusy(true);
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      for (let i = 0; i < refs.current.length; i++) {
        if (!refs.current[i]) continue;
        await exportOne(refs.current[i], `${i + 1}-${frames[i].name}`);
        await new Promise((r) => setTimeout(r, 500));
      }
    } catch (e) {
      console.error('Story export failed:', e);
      alert('No se pudieron generar las imágenes. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  }, [exportOne, frames]);

  if (!s) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md text-white text-sm font-manrope hover:bg-white/20 transition"
        data-testid="eoae-stories-btn"
      >
        <Share2 className="w-4 h-4" /> Historias
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col" data-testid="eoae-stories-modal">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <h3 className="font-oswald uppercase tracking-widest text-[#D4AF37] text-lg">Historias para compartir</h3>
              <p className="font-manrope text-white/60 text-sm">Formato vertical 9:16 · listo para Instagram y WhatsApp</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={downloadAll}
                disabled={busy}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#e3c14f] transition disabled:opacity-60"
                data-testid="eoae-stories-download-all"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {busy ? 'Generando…' : `Descargar todas (${frames.length})`}
              </button>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-2" data-testid="eoae-stories-close">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <div className="flex gap-6 p-8 h-full items-center" style={{ width: 'max-content' }}>
              {frames.map((f, i) => (
                <div key={f.name} className="flex-shrink-0 flex flex-col items-center gap-3">
                  <div
                    className="rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-2xl"
                    style={{ width: FRAME_W * 0.24, height: FRAME_H * 0.24 }}
                  >
                    <div style={{ transform: 'scale(0.24)', transformOrigin: 'top left' }}>
                      <div ref={(el) => (refs.current[i] = el)}>{f.node}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => exportOne(refs.current[i], `${i + 1}-${f.name}`)}
                    className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-manrope"
                  >
                    <Download className="w-3.5 h-3.5" /> {f.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StoryShareButton;
